<template>
  <div
    :class="sidebarbg"
    class="app-sidebar sidebar-shadow bg-dark text-lighter"
    @mouseleave="toggleSidebarHover('remove', 'closed-sidebar-open')"
  >
    <div class="app-header__logo">
      <div class="logo-src">
        <svg viewBox="0 0 32 32">
          <path
            d="M0,7.5l4,-2.5v22l-4,-2.5zM28,5l4,2.5v17l-4,2.5zM12,0v9h8v-9l4,2.5v10.5h-16v-10.5zM8,17h16v12.5l-4,2.5v-11h-8v11l-4,-2.5z"
          ></path>
        </svg>
        <span>Hotelium</span>
      </div>
    </div>
    <div class="app-sidebar-content scroll-area" v-if="menu">
      <div class="menu-sticky-wrap">
        <div class="admin-menu-wrap mb-3">
          <div class="v-sidebar-menu sidebar-margin vsm-default admin-menu">
            <div class="vsm-list" v-if="isAdmin">
              <div class="vsm-header">Admin</div>
              <div class="vsm-item first-item">
                <a class="vsm-link" href="/admin/" tabindex="">
                  <i class="vsm-icon pe-7s-unlock"></i>
                  <span class="vsm-title">Admin</span>
                </a>
              </div>
              <div class="vsm-item first-item">
                <a class="vsm-link" v-on:click="adminClick()" tabindex="">
                  <i class="vsm-icon pe-7s-unlock"></i>
                  <span class="vsm-title">Admin v2</span>
                </a>
              </div>
              <div class="vsm-item first-item">
                <a class="vsm-link" v-on:click="vouchersClick()" tabindex="">
                  <i class="vsm-icon pe-7s-unlock"></i>
                  <span class="vsm-title">Vouchers</span>
                </a>
              </div>
              <div class="vsm-item first-item">
                <a
                  class="vsm-link"
                  v-on:click="reservationsClick()"
                  tabindex=""
                >
                  <i class="vsm-icon pe-7s-unlock"></i>
                  <span class="vsm-title">Reservations</span>
                </a>
              </div>
            </div>
          </div>
        </div>
        <sidebar-menu
          :menu="menu"
          @item-click="onItemClick"
          class="sidebar-margin"
          :show-child="true"
          :show-one-child="false"
        >
        </sidebar-menu>

        <div class="admin-menu-wrap mb-3">
          <div class="v-sidebar-menu sidebar-margin vsm-default admin-menu">
            <div class="vsm-list">
              <div class="vsm-header"></div>
              <div class="vsm-header"></div>
              <div class="vsm-header"></div>
              <div class="vsm-header"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

// ==========================================================================
// STEP 1 - PRE-EMPTIVE module-level patch of vue-sidebar-menu's shared
// itemMixin, BEFORE we ever require() SidebarMenu (and transitively Item
// and mixin). ES6 `import` statements are hoisted to the top and would
// load SidebarMenu (and its Item.vue -> mixin.js) BEFORE any of our code
// runs, so we deliberately switch to `require()` below to preserve order.
//
// Why patch the shared mixin object at all when we also pass :show-child
// and :show-one-child props further down? Triple safety net:
//   - Props cover the normal top-level menu render path
//   - Mixin method patching covers click races / route sync / i18n re-renders
//   - DOM guards later cover any case that falls through both layers
// ==========================================================================
try {
  // eslint-disable-next-line global-require
  const mixinModule = require("vue-sidebar-menu/src/mixin");
  const sharedItemMixin = mixinModule.itemMixin;
  if (sharedItemMixin && sharedItemMixin.methods) {
    const origInitShowState = sharedItemMixin.methods.initShowState;
    sharedItemMixin.methods.initShowState = function htlPatchedInitShowState() {
      if (origInitShowState) origInitShowState.call(this);
      if (this.item && this.item.child) {
        this.itemShow = true;
        if (this._uid) {
          try {
            this.$set(this, "activeShow", this._uid);
          } catch (e) {
            // ignore: activeShow fallback only used in showOneChild mode
          }
        }
      }
    };

    const origClickEvent = sharedItemMixin.methods.clickEvent;
    sharedItemMixin.methods.clickEvent = function htlPatchedClickEvent(event) {
      if (origClickEvent) origClickEvent.call(this, event);
      // Immediately revert any `this.itemShow = !this.itemShow` toggle the
      // original handler just applied (mixin.js line 68), so a parent link
      // click can navigate (router-link / href) but never collapses children.
      if (this.item && this.item.child) {
        this.itemShow = true;
        if (this._uid) {
          try {
            this.$set(this, "activeShow", this._uid);
          } catch (e) {
            // ignore: activeShow fallback only used in showOneChild mode
          }
        }
      }
    };
  }
} catch (e) {
  // require path might change across library versions; non-fatal because
  // the template props and DOM guards catch the same cases anyway.
}

// STEP 2 - Now load SidebarMenu. Item.vue (loaded transitively) will see
// the patched itemMixin.methods references because we have not imported
// SidebarMenu anywhere else with an ES6 hoisted import in this file.
// eslint-disable-next-line global-require
const { SidebarMenu } = require("vue-sidebar-menu");

export default {
  components: {
    SidebarMenu,
  },
  data() {
    return {
      showAdmin: false,
      sidebarActive: false,
      htlObserver: null,
      htlInterval: null,
      htlObserverThrottle: null,
      htlSidebarClickListener: null,

      menu: [
        {
          icon: "pe-7s-graph2",
          header: true,
          title: this.$i18n.t("menu.hotelium"),
          hiddenOnCollapse: false,
        },
        {
          icon: "pe-7s-graph2",
          title: this.$i18n.t("menu.dashboard"),
          href: "/",
        },
        {
          icon: "pe-7s-user",
          title: this.$i18n.t("menu.profile"),
          href: "/profile",
        },
        {
          icon: "pe-7s-wallet",
          title: this.$i18n.t("menu.wallet"),
          href: "/wallet",
          collapsible: false,
          child: [
            {
              title: this.$i18n.t("menu.withdraw"),
              href: "/withdraw",
            },
          ],
        },
        // {
        //     icon: "pe-7s-repeat",
        //     title: this.$i18n.t("menu.swap"),
        //     href: "/swap"
        // },
        {
          icon: "pe-7s-piggy",
          title: this.$i18n.t("menu.buy"),
          href: "/buy-packages",
        },
        {
          icon: "pe-7s-cart",
          title: this.$i18n.t("menu.packages"),
          href: "/packages",
        },
        {
          icon: "pe-7s-safe",
          title: this.$i18n.t("buy-freedom-program"),
          href: "/freedom-program",
        },
        {
          icon: "pe-7s-leaf",
          title: this.$i18n.t("my-freedom-program"),
          href: "/my-freedom-program",
        },
        {
          icon: "pe-7s-plane",
          title: this.$i18n.t("menu.resort-access"),
          href: "/travels",
        },
        {
          icon: "pe-7s-global",
          title: this.$i18n.t("menu.my-resort-access"),
          href: "/my-travels",
        },
        {
          icon: "pe-7s-portfolio",
          title: this.$i18n.t("menu.pool"),
          href: "/pool",
        },
        {
          icon: "pe-7s-medal",
          title: "VIP",
          href: "/vip",
        },
        {
          header: true,
          title: this.$i18n.t("menu.org"),
        },
        {
          icon: "pe-7s-share",
          title: this.$i18n.t("menu.unilevel"),
          href: "/unilevel",
        },
        {
          icon: "pe-7s-network",
          title: this.$i18n.t("menu.binary"),
          href: "/binary",
        },
        {
          icon: "pe-7s-users",
          title: this.$i18n.t("menu.downline"),
          href: "/downline",
        },
        {
          icon: "pe-7s-box2",
          title: this.$i18n.t("menu.all-packages"),
          href: "/all-packages",
        },
        {
          icon: "pe-7s-graph1",
          title: this.$i18n.t("menu.statistics"),
          href: "/statistics",
        },

        {
          header: true,
          title: this.$i18n.t("menu.info"),
        },
        {
          icon: "pe-7s-diamond",
          title: this.$i18n.t("menu.diamond-club"),
          href: "/diamond-club-zlatibor",
        },
        {
          icon: "lnr-apartment",
          title: this.$i18n.t("menu.real-estate"),
          href: "/real-estate",
        },
        {
          icon: "lnr-heart-pulse",
          title: this.$i18n.t("menu.hearts"),
          href: "/hearts",
        },
        {
          icon: "lnr-graduation-hat",
          title: this.$i18n.t("menu.academy"),
          href: "/academy",
        },
        {
          icon: "lnr-book",
          title: this.$i18n.t("menu.documents"),
          href: "/documents",
        },
        {
          icon: "lnr-bubble",
          title: this.$i18n.t("menu.support"),
          href: "/support",
        },
        {
          icon: "pe-7s-shopbag",
          title: this.$i18n.t("menu.shop"),
          href: "/shop",
        },
      ],
      menu2: [
        {
          header: true,
          title: "Admin",
          disabled: !this.isAdmin,
        },
        {
          icon: "lnr-lock",
          title: "Admin",
          href: "/admin",
          disabled: !this.isAdmin,
        },
        {
          icon: "lnr-lock",
          title: "Admin Vouchers",
          href: "/admin-vouchers",
          disabled: !this.isAdmin,
        },
        {
          icon: "lnr-lock",
          title: "test",
          href: "/8d8b",
          disabled: !this.isAdmin,
        },
      ],
      collapsed: false,

      windowWidth: 0,
    };
  },
  computed: {
    ...mapGetters({
      isOpen: "menu/isOpen",
      isAdmin: "user/isAdmin",
    }),
  },
  props: {
    sidebarbg: String,
  },
  methods: {
    patchAndForceChildMenusOpen() {
      this.$nextTick(() => {
        const sidebar = this.$el.querySelector(".v-sidebar-menu.sidebar-margin");
        if (!sidebar) return;
        const items = sidebar.querySelectorAll(".vsm-item");
        items.forEach((itemEl) => {
          // Access the Vue Item component instance directly via __vue__
          // (each .vsm-item is the root element of vue-sidebar-menu/src/components/Item.vue)
          const vm = itemEl.__vue__;
          if (vm && vm.item && vm.item.child && typeof vm.itemShow !== "undefined") {
            // 1) FORCE internal state to open permanently.
            //    This makes the `show` computed return true, which keeps the
            //    `v-if="show"` node in the DOM instead of removing it entirely.
            vm.itemShow = true;
            if (vm.activeShow !== undefined && vm._uid) {
              vm.$set(vm, "activeShow", vm._uid);
            }

            // 2) PATCH clickEvent on this instance so that after the default
            //    vue-sidebar-menu toggle (this.itemShow = !this.itemShow) runs,
            //    we IMMEDIATELY reset it back to true. Patching the instance
            //    method is more reliable than relying on setTimeout race.
            if (!vm.htlClickPatched) {
              const origClickEvent = vm.clickEvent.bind(vm);
              vm.clickEvent = function (event) {
                origClickEvent(event);
                vm.itemShow = true;
                if (vm._uid) {
                  try {
                    vm.$set(vm, "activeShow", vm._uid);
                  } catch (e) {
                    // swallow: activeShow fallback not strictly required
                  }
                }
                vm.$nextTick(() => this.applyInlineVisibleStyles(sidebar));
              }.bind(vm);
              vm.htlClickPatched = true;
            }

            if (!vm.htlInitPatched && typeof vm.initShowState === "function") {
              const origInit = vm.initShowState.bind(vm);
              vm.initShowState = function () {
                origInit();
                vm.itemShow = true;
                if (vm._uid) {
                  try {
                    vm.$set(vm, "activeShow", vm._uid);
                  } catch (e) {
                    // swallow: activeShow fallback not strictly required
                  }
                }
              }.bind(vm);
              vm.htlInitPatched = true;
              vm.initShowState();
            }
          }

          // 4) Also apply inline !important styles as a second-layer defence
          //    (in case v-if kept the node but classes still collapse height).
          const link = itemEl.querySelector(":scope > .vsm-link");
          let wrapper = itemEl.querySelector(":scope > .vsm-dropdown");
          if (!wrapper && link) {
            const sibling =
              link.nextElementSibling &&
              link.nextElementSibling !== itemEl.querySelector(":scope > .vsm-arrow")
                ? link.nextElementSibling
                : null;
            if (sibling) {
              wrapper = sibling.classList.contains("vsm-dropdown")
                ? sibling
                : sibling.querySelector(".vsm-dropdown");
            }
          }
          if (!wrapper) wrapper = itemEl.querySelector(".vsm-dropdown");
          if (wrapper) {
            wrapper.style.setProperty("display", "block", "important");
            wrapper.style.setProperty("height", "auto", "important");
            wrapper.style.setProperty("max-height", "none", "important");
            wrapper.style.setProperty("overflow", "visible", "important");
            wrapper.style.setProperty("visibility", "visible", "important");
            wrapper.style.setProperty("opacity", "1", "important");

            const list = wrapper.querySelector(":scope > .vsm-list");
            if (list) {
              list.style.setProperty("display", "block", "important");
              list.style.setProperty("height", "auto", "important");
              list.style.setProperty("max-height", "none", "important");
              list.style.setProperty("overflow", "visible", "important");
            }
          }
        });
      });
    },
    applyInlineVisibleStyles(sidebar) {
      if (!sidebar) sidebar = this.$el.querySelector(".v-sidebar-menu.sidebar-margin");
      if (!sidebar) return;
      sidebar.querySelectorAll(".vsm-dropdown").forEach((wrapper) => {
        wrapper.style.setProperty("display", "block", "important");
        wrapper.style.setProperty("height", "auto", "important");
        wrapper.style.setProperty("max-height", "none", "important");
        wrapper.style.setProperty("overflow", "visible", "important");
        wrapper.style.setProperty("visibility", "visible", "important");
        wrapper.style.setProperty("opacity", "1", "important");
        const list = wrapper.querySelector(":scope > .vsm-list");
        if (list) {
          list.style.setProperty("display", "block", "important");
          list.style.setProperty("height", "auto", "important");
          list.style.setProperty("max-height", "none", "important");
          list.style.setProperty("overflow", "visible", "important");
        }
      });
    },
    // Legacy alias kept so any older call sites still work
    forceChildMenusOpen() {
      this.patchAndForceChildMenusOpen();
    },
    onItemClick(item) {
      this.sidebarActive = false;
      this.$store.commit("menu/closeMenu");

      const el = document.body;
      el.classList.remove("closed-sidebar-open");
      const btn = document.getElementById("mobile-menu-btn");
      if (btn) btn.classList.remove("is-active");

      // Always keep child menus visible, even after a parent click.
      this.$nextTick(() => {
        this.patchAndForceChildMenusOpen();
        setTimeout(() => this.patchAndForceChildMenusOpen(), 0);
      });
    },
    toggleBodyClass(className) {
      const el = document.body;
      this.isOpen = !this.isOpen;

      if (this.isOpen) {
        el.classList.add(className);
      } else {
        el.classList.remove(className);
      }
    },
    toggleSidebarHover(add, className) {
      const el = document.body;
      this.sidebarActive = !this.sidebarActive;

      this.windowWidth = document.documentElement.clientWidth;

      if (this.windowWidth > "992") {
        if (add === "add") {
          el.classList.add(className);
        } else {
          el.classList.remove(className);
        }
      }
    },
    getWindowWidth() {
      const el = document.body;

      this.windowWidth = document.documentElement.clientWidth;

      if (this.windowWidth < "769") {
        el.classList.add("closed-sidebar");
      } else {
        el.classList.remove("closed-sidebar", "closed-sidebar-md");
      }
    },
    adminClick() {
      window.location.href =
        "adminv2?t=" + localStorage.getItem("apollo-token");
    },
    vouchersClick() {
      this.$router.push("/admin-vouchers");
    },
    reservationsClick() {
      this.$router.push("/admin-reservations");
    },
  },
  mounted() {
    this.$nextTick(function () {
      window.addEventListener("resize", this.getWindowWidth);

      this.getWindowWidth();

      this.patchAndForceChildMenusOpen();
      setTimeout(() => this.patchAndForceChildMenusOpen(), 50);
      setTimeout(() => this.patchAndForceChildMenusOpen(), 100);
      setTimeout(() => this.patchAndForceChildMenusOpen(), 200);
      setTimeout(() => this.patchAndForceChildMenusOpen(), 500);
      setTimeout(() => this.patchAndForceChildMenusOpen(), 1000);

      // Add capture-phase DOM click listener on sidebar so that even if
      // instance patching somehow missed, we re-apply immediately after
      // any click inside the sidebar menu runs its course.
      const sidebar = this.$el.querySelector(".v-sidebar-menu.sidebar-margin");
      if (sidebar) {
        const reapply = () => {
          this.patchAndForceChildMenusOpen();
          setTimeout(() => this.patchAndForceChildMenusOpen(), 0);
        };
        sidebar.addEventListener("click", reapply, true);
        this.htlSidebarClickListener = reapply;

        if (typeof MutationObserver !== "undefined") {
          try {
            const observer = new MutationObserver(() => {
              if (this.htlObserverThrottle) return;
              this.htlObserverThrottle = setTimeout(() => {
                this.htlObserverThrottle = null;
                this.patchAndForceChildMenusOpen();
              }, 50);
            });
            observer.observe(sidebar, {
              childList: true,
              subtree: true,
              attributes: true,
              attributeFilter: ["class", "style"],
            });
            this.htlObserver = observer;
          } catch (e) {
            // MutationObserver not supported; fallback to interval only
          }
        }
      }

      this.htlInterval = setInterval(() => {
        this.patchAndForceChildMenusOpen();
      }, 1500);
    });
  },

  updated() {
    this.$nextTick(() => {
      this.patchAndForceChildMenusOpen();
      setTimeout(() => this.patchAndForceChildMenusOpen(), 0);
    });
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.getWindowWidth);
    const sidebar = this.$el && this.$el.querySelector(".v-sidebar-menu.sidebar-margin");
    if (sidebar && this.htlSidebarClickListener) {
      sidebar.removeEventListener("click", this.htlSidebarClickListener, true);
    }
    if (this.htlObserver && typeof this.htlObserver.disconnect === "function") {
      this.htlObserver.disconnect();
      this.htlObserver = null;
    }
    if (this.htlInterval) {
      clearInterval(this.htlInterval);
      this.htlInterval = null;
    }
    if (this.htlObserverThrottle) {
      clearTimeout(this.htlObserverThrottle);
      this.htlObserverThrottle = null;
    }
  },
};
</script>

<style>
.ps-container > .ps__scrollbar-y-rail {
  opacity: 0.8;
  display: block;
}
</style>
